import { BookingPosition, BookingResult, IncomingTransfer } from '../types';
import { ModelAdapters } from '../interface/model-adapters';
import { StockMovementModel } from 'src/packages/warehouse';

/**
 * Empfangsbestätigung (Phase 2) eingehender Umlagerungen durch den Empfänger.
 * Zwei Pfade je nach Ziel-Welt:
 * - `acceptBufferstock`: Bufferstock-Ziel, erlaubt Teil-Annahme - der
 *   Annehmende bucht die Differenz als eigenes NormalMovement zurück zur
 *   Quelle.
 * - `acceptCar`: KFZ-Ziel, erlaubt Teil-Annahme - die Differenz
 *   geht als CarRelocationMovement zurück ans Quell-KFZ; lokal wird nur das
 *   Ziel-KFZ gespiegelt (die Quelle heilt über den Backend-Pull).
 *
 * Retry-Sicherheit: Differenz-Rückbuchung läuft ZUERST (deterministische
 * `client_id` dedupliziert serverseitig), danach der Phase-2-Commit des
 * eingehenden Movements (idempotent). Abbruch an jeder Stelle ist gefahrlos
 * wiederholbar; die Rückbuchungs-Phase-2 ist über Pending-Marker abgesichert.
 */
export class TransferAcceptance {
  /**
   * Bestätigt ein eingehendes Bufferstock-Movement ganz oder teilweise. Lokale
   * Spiegel werden erst nach Remote-Erfolg angepasst; ein fehlgeschlagenes
   * Spiegel-Update bleibt Erfolg (der Down-Sync gleicht ab).
   *
   * @param movement Eingehendes Movement (aus `findIncomingBufferstockTransfers`)
   * @param acceptedAmount Angenommene Menge (0 ≤ x ≤ movement.amount; 0 = Voll-Ablehnung)
   * @param adapters ModelAdapters
   */
  public async acceptBufferstock(
    movement: IncomingTransfer,
    acceptedAmount: number,
    adapters: ModelAdapters,
  ): Promise<BookingResult> {
    if (
      movement.id === undefined ||
      movement.target_id === undefined ||
      movement.article_id === undefined
    ) {
      return this.failure(
        'Empfangsbestätigung: Movement ohne id/target_id/article_id',
      );
    }
    // 0 ist erlaubt: Voll-Ablehnung (z.B. Fehllieferung) - das eingehende
    // Movement wird committet und die komplette Menge zurückgebucht.
    if (acceptedAmount < 0 || acceptedAmount > movement.amount) {
      return this.failure(
        `Empfangsbestätigung: angenommene Menge muss zwischen 0 und ${movement.amount} liegen`,
      );
    }
    const difference = movement.amount - acceptedAmount;
    if (difference > 0 && movement.related_source_id === undefined) {
      return this.failure(
        'Empfangsbestätigung: related_source_id fehlt für die Differenz-Rückbuchung',
      );
    }

    // Selbstheilung: liegengebliebene Phase-2-Commits nachholen. Fehlschläge
    // lassen die Marker liegen und blockieren die Annahme nicht.
    await adapters.pendingCommit.retryAll();

    // 1. Teil-Annahme: Differenz-Rückbuchung ZUERST (siehe Klassen-Doku).
    let returnPhase2Pending = false;
    if (difference > 0) {
      const returnMovement = adapters.movement.buildTransferReturn(
        movement,
        difference,
      );
      const savedReturn = await adapters.movement.createRemote(returnMovement);
      if (!savedReturn.value || savedReturn.value.id === undefined) {
        // Noch nichts committet → vollständig wiederholbar.
        return this.failure(
          'Differenz-Rückbuchung konnte nicht gespeichert werden - Annahme bitte erneut versuchen',
          savedReturn.retryable,
        );
      }
      const savedReturnId = savedReturn.value.id;
      // Phase 2 der Rückbuchung, durch Pending-Marker abgesichert.
      // alreadyBooked (409) = eigener Retry hat schon committet → Erfolg.
      adapters.pendingCommit.add(savedReturnId, returnMovement.client_id);
      const returnBooked = await adapters.movement.bookRemote(savedReturnId);
      if (returnBooked.booked || returnBooked.alreadyBooked) {
        adapters.pendingCommit.remove(savedReturnId);
      } else {
        // Marker bleibt liegen und wird nachgeholt; die Annahme selbst kann
        // fortgesetzt werden.
        returnPhase2Pending = true;
      }
    }

    // 2. Phase-2-Commit des eingehenden Movements. Das Backend bucht das
    //    related movement der übergebenen ID, daher die related_movement_id
    //    (Fallback: eigene ID).
    const movementIdToBook = movement.related_movement_id ?? movement.id;
    const booked = await adapters.movement.bookRemote(movementIdToBook);
    if (booked.alreadyBooked) {
      // Anderes Gerät/anderer User war schneller (Backend-409-Guard):
      // permanenter Konflikt, die Empfangsliste muss neu geladen werden.
      return this.failure(
        'Movement wurde bereits bestätigt (anderes Gerät?) - Liste bitte aktualisieren',
      );
    }
    if (!booked.booked) {
      // Rückbuchung ist ggf. schon durch - Wiederholung ist dank
      // deterministischer client_id und idempotentem bookRemote gefahrlos.
      return this.failure(
        'Empfangsbestätigung konnte nicht gebucht werden - bitte erneut versuchen',
        booked.retryable,
      );
    }

    // 3. Lokale Bestands-Spiegel (erst nach Remote-Erfolg; Fehlschläge
    //    werden vom Down-Sync ausgeglichen).
    if (acceptedAmount > 0) {
      await adapters.bufferstock.updatePositionAmountLocal(
        this.positionOf(movement, acceptedAmount),
        movement.target_id,
        +acceptedAmount,
      );
    }
    if (difference > 0 && movement.related_source_id !== undefined) {
      await adapters.bufferstock.updatePositionAmountLocal(
        this.positionOf(movement, difference),
        movement.related_source_id,
        +difference,
      );
    }

    // 4. Lokale Kopien beider Movement-Legs aufräumen, damit die
    //    Empfangsliste sie nicht erneut anzeigt (UI-Hygiene; Fehlschläge
    //    toleriert, Down-Sync verwirft verifizierte Movements ohnehin).
    await adapters.movement.removeLocal(movement.id);
    if (movement.related_movement_id !== undefined) {
      await adapters.movement.removeLocal(movement.related_movement_id);
    }

    return {
      success: true,
      message: returnPhase2Pending
        ? 'Annahme gebucht; Phase 2 der Differenz-Rückbuchung wird nachgeholt'
        : '',
    };
  }

  /**
   * Bestätigt eine eingehende KFZ-Umlagerung ganz oder teilweise (Phase 2).
   * Bei Teil-Annahme wird die Differenz self-verified zurück ans Quell-KFZ
   * gebucht: ein CarRelocationMovement Ziel->Quell, das der
   * Annehmende selbst durch beide Phasen treibt. Lokaler Spiegel ist nur das
   * eingeloggte Ziel-Fahrzeug (+acceptedAmount); die Quell-Seite heilt über den
   * Backend-Pull des Quell-KFZ (der CarAdapter kennt nur das eingeloggte
   * Fahrzeug). Retry-Sicherheit und Reihenfolge wie `acceptBufferstock`.
   *
   * @param movement Eingehendes Movement (aus `findIncomingCarTransfers`)
   * @param acceptedAmount Angenommene Menge (0 ≤ x ≤ movement.amount)
   * @param adapters ModelAdapters
   */
  public async acceptCar(
    movement: IncomingTransfer,
    acceptedAmount: number,
    adapters: ModelAdapters,
  ): Promise<BookingResult> {
    if (
      movement.id === undefined ||
      movement.target_id === undefined ||
      movement.article_id === undefined
    ) {
      return this.failure(
        'Empfangsbestätigung: Movement ohne id/target_id/article_id',
      );
    }
    // 0 ist erlaubt: Voll-Ablehnung - das eingehende Movement wird committet
    // und die komplette Menge zurückgebucht.
    if (acceptedAmount < 0 || acceptedAmount > movement.amount) {
      return this.failure(
        `Empfangsbestätigung: angenommene Menge muss zwischen 0 und ${movement.amount} liegen`,
      );
    }
    const difference = movement.amount - acceptedAmount;
    if (difference > 0 && movement.related_source_id === undefined) {
      return this.failure(
        'Empfangsbestätigung: related_source_id fehlt für die Differenz-Rückbuchung',
      );
    }

    // Selbstheilung: liegengebliebene Phase-2-Commits nachholen. Fehlschläge
    // lassen die Marker liegen und blockieren die Annahme nicht.
    await adapters.pendingCommit.retryAll();

    // 1. Teil-Annahme: Differenz-Rückbuchung ZUERST (siehe acceptBufferstock).
    let returnPhase2Pending = false;
    if (difference > 0) {
      const returnMovement = adapters.movement.buildCarRelocationReturn(
        movement,
        difference,
      );
      const savedReturn = await adapters.movement.createRemote(returnMovement);
      if (!savedReturn.value || savedReturn.value.id === undefined) {
        // Noch nichts committet → vollständig wiederholbar.
        return this.failure(
          'Differenz-Rückbuchung konnte nicht gespeichert werden - Annahme bitte erneut versuchen',
          savedReturn.retryable,
        );
      }
      const savedReturnId = savedReturn.value.id;
      // Phase 2 der Rückbuchung, durch Pending-Marker abgesichert.
      // alreadyBooked (409) = eigener Retry hat schon committet → Erfolg.
      adapters.pendingCommit.add(savedReturnId, returnMovement.client_id);
      const returnBooked = await adapters.movement.bookRemote(savedReturnId);
      if (returnBooked.booked || returnBooked.alreadyBooked) {
        adapters.pendingCommit.remove(savedReturnId);
      } else {
        returnPhase2Pending = true;
      }
    }

    // 2. Phase-2-Commit des eingehenden Movements (TransferPoint -> Ziel-KFZ). Das
    // Backend bucht das related movement der übergebenen ID (Fallback: eigene ID).
    const movementIdToBook = movement.related_movement_id ?? movement.id;
    const booked = await adapters.movement.bookRemote(movementIdToBook);
    if (booked.alreadyBooked) {
      return this.failure(
        'Movement wurde bereits bestätigt (anderes Gerät?) - Liste bitte aktualisieren',
      );
    }
    if (!booked.booked) {
      return this.failure(
        'Empfangsbestätigung konnte nicht gebucht werden - bitte erneut versuchen',
        booked.retryable,
      );
    }

    // 3. Lokaler Spiegel: nur das eingeloggte Ziel-KFZ (+acceptedAmount). Die
    //    Quell-Seite hat KEINEN lokalen Spiegel - der CarAdapter adressiert nur
    //    das eingeloggte Fahrzeug; das Quell-KFZ heilt über seinen Backend-Pull.
    if (acceptedAmount > 0) {
      await adapters.car.updatePositionAmountLocal(
        this.positionOf(movement, acceptedAmount),
        +acceptedAmount,
      );
    }

    // 4. Lokale Kopien beider Movement-Legs aufräumen (UI-Hygiene).
    await adapters.movement.removeLocal(movement.id);
    if (movement.related_movement_id !== undefined) {
      await adapters.movement.removeLocal(movement.related_movement_id);
    }

    return {
      success: true,
      message: returnPhase2Pending
        ? 'Annahme gebucht; Phase 2 der Differenz-Rückbuchung wird nachgeholt'
        : '',
    };
  }

  /**
   * Baut die minimale Position (article_id + amount) für die lokalen
   * Spiegel-Updates.
   *
   * @param movement Movement, aus dem die article_id übernommen wird
   * @param amount Menge der zu spiegelnden Position
   * @returns Position mit article_id des Movements und der übergebenen Menge
   */
  private positionOf(
    movement: StockMovementModel,
    amount: number,
  ): BookingPosition {
    return {
      article_id: movement.article_id,
      amount,
    } as BookingPosition;
  }

  private failure(message: string, retryable: boolean = false): BookingResult {
    return { success: false, message, retryable };
  }
}
